/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { cloneElement, useEffect, useState } from 'react'
import SiteShell from './SiteShell.jsx'
import { GLYPHS } from '../components/MobileMenu.jsx'
import { buildGuide } from '../components/pwaGuide.js'
import { guardCall } from '../lib/officeHours.js'

const LINKS = [
  { href: 'https://app.sequoia.fitness', icon: 'store_icon.webp', label: 'پلتفرم هوشمند سکویا فیتنس', small: '(وب‌اپلیکیشن)', sub: 'app.sequoia.fitness', primary: true, accordion: true },
  { href: 'https://sequoia.fitness', icon: 'website-icon.webp', label: 'وب‌سایت سکویا فیتنس', sub: 'sequoia.fitness' },
  { href: '/academy/', src: '/a/sequoia-academy-ico.webp', label: 'سکویا آکادمی', sub: 'sequoia.fitness/academy' },
  { href: 'https://www.youtube.com/@Sequoia.fitness', icon: 'ytube-icon.webp', label: 'یوتیوب', sub: 'youtube.com/@Sequoia.fitness' },
  { href: 'https://instagram.com/app_sequoia', icon: 'instagram-icon.webp', label: 'اینستاگرام', sub: 'instagram.com/app_sequoia' },
  { href: 'https://t.me/sequoiafitness_channel', icon: 'tlgrm-icon.webp', label: 'کانال تلگرام', sub: 't.me/sequoiafitness_channel' },
  { href: 'tel:+982126254146', icon: 'phone.webp', label: 'تماس با پشتیبانی', sub: '021 - 26 25 41 46 • شنبه تا چهارشنبه ۹ تا ۱۷ ، پنجشنبه ۹ تا ۱۳', tel: true },
  { href: '/sequoia.vcf', label: 'اضافه کردن سکویا به مخاطبان', sub: 'ذخیره‌ی شماره و همه‌ی لینک‌ها', contact: true },
]

function ContactIcon() {
  return (
    <span className="pg-link__ico" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="8" r="3.5" />
        <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
        <path d="M19 6v6M16 9h6" />
      </svg>
    </span>
  )
}

function AppAccordion({ l }) {
  const [open, setOpen] = useState(false)
  const [web, setWeb] = useState(false)
  const [guide, setGuide] = useState(null)

  useEffect(() => {
    setGuide(buildGuide())
  }, [])

  return (
    <li className={'pg-acc-li' + (open ? ' is-open' : '')}>
      <button type="button" className="pg-link is-primary is-acc" aria-expanded={open} aria-controls="pg-app-panel" onClick={() => setOpen(!open)}>
        <img src={'/p/' + l.icon} alt="" width="56" height="56" loading="lazy" />
        <span className="pg-link__txt">
          <b>
            {l.label} {l.small && <small>{l.small}</small>}
          </b>
          <span className="pg-link__row">
            <span dir="ltr">{l.sub}</span>
            <i className="pg-chev" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </i>
          </span>
        </span>
      </button>
      <div className={'pg-acc' + (open ? ' is-open' : '')} id="pg-app-panel" inert={!open}>
        <div className="pg-acc__in">
          <div className="pg-sub-row">
            <a className="pg-sub pg-sub--apk" href="/a/SEQUOIA-FITNESS-V1.apk" download>
              دانلود نسخه اندروید
            </a>
            <button type="button" className="pg-sub pg-sub--web" aria-expanded={web} aria-controls="pg-web-guide" onClick={() => setWeb(!web)}>
              نسخه‌ی وب اپلیکیشن
            </button>
          </div>
          <div className={'mm-pwa-guide' + (web ? ' is-open' : '')} id="pg-web-guide" inert={!web}>
            <div className="mm-pwa-in">
              <div className="mm-pwa-title">{guide ? guide.title : ''}</div>
              <ol className="mm-pwa-steps">
                {guide &&
                  guide.steps.map((parts, i) => (
                    <li key={i}>{parts.map((p, j) => (GLYPHS[p] ? cloneElement(GLYPHS[p], { key: j }) : p))}</li>
                  ))}
              </ol>
              <a className="mm-pwa-open" href="https://app.sequoia.fitness" target="_blank" rel="noopener">
                باز کردن وب‌اپلیکیشن
              </a>
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}

export default function LinksPage() {
  return (
    <SiteShell variant="links" bare>
      <section className="pg-links">
        <header className="pg-links__head">
          <img className="pg-links__logo" src="/p/q-logo-green.svg" alt="سکویا" width="96" height="73" />
          <h1>تماس با ما</h1>
        </header>
        <ul>
          {LINKS.map((l) =>
            l.accordion ? (
              <AppAccordion key={l.label} l={l} />
            ) : (
            <li key={l.label}>
              <a className={'pg-link' + (l.primary ? ' is-primary' : '') + (l.contact ? ' is-contact' : '')} href={l.href} {...(l.tel || l.contact ? {} : { target: '_blank', rel: 'noopener' })} {...(l.tel ? { onClick: guardCall } : {})}>
                {l.contact ? <ContactIcon /> : <img src={l.src || '/p/' + l.icon} alt="" width="56" height="56" loading="lazy" />}
                <span className="pg-link__txt">
                  <b>
                    {l.label} {l.small && <small>{l.small}</small>}
                  </b>
                  <span dir="ltr">{l.sub}</span>
                </span>
                {l.badge && <em>{l.badge}</em>}
              </a>
            </li>
            ),
          )}
        </ul>
      </section>
    </SiteShell>
  )
}
